import { create } from 'zustand';
import type { UserAccount } from '../types';

export interface Announcement {
  id: string;
  title: string;
  message: string;
  type: string;
}

interface SessionState {
  // State
  user: UserAccount | null;
  /**
   * Plan 3 (#778) — afgeleid van `user.organization?.id` bij elke setUser-call.
   * Componenten die alleen de id nodig hebben (AppSidebar, /organisatie route)
   * kunnen `organizationId` lezen zonder over de hele user-payload te leunen.
   * useOrganizationStore gebruikt deze id om lazy de detail te laden.
   */
  organizationId: string | null;
  loading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pendingInvitations: any[];
  appVersion: string;
  announcements: Announcement[];
  dismissedAnnouncements: Set<string>;

  // Actions
  setUser: (user: UserAccount | null) => void;
  setLoading: (loading: boolean) => void;
  setPendingInvitations: (invitations: any[]) => void;
  setAppVersion: (version: string) => void;
  setAnnouncements: (announcements: Announcement[]) => void;
  dismissAnnouncement: (id: string) => void;
  logout: () => void;
}

const loadDismissedAnnouncements = (): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem('dismissed_announcements') || '[]'));
  } catch {
    return new Set();
  }
};

export const useSessionStore = create<SessionState>((set) => ({
  // Initial state
  user: null,
  organizationId: null,
  loading: true,
  pendingInvitations: [],
  appVersion: '',
  announcements: [],
  dismissedAnnouncements: loadDismissedAnnouncements(),

  // Actions
  setUser: (user) => {
    // #466: bij succesvolle login de intent-logout-flag wissen, anders zou een
    // directe F5 na login alsnog de user uitloggen.
    if (user) {
      try {
        localStorage.removeItem('af_intent_logged_out');
      } catch {
        /* ignore */
      }
    }
    // Plan 3 (#778) — synchroniseer organizationId met user.organization?.id.
    set({ user, organizationId: user?.organization?.id ?? null });
  },
  setLoading: (loading) => set({ loading }),
  setPendingInvitations: (pendingInvitations) => set({ pendingInvitations }),
  setAppVersion: (appVersion) => set({ appVersion }),
  setAnnouncements: (announcements) => set({ announcements }),

  dismissAnnouncement: (id) =>
    set((state) => {
      const next = new Set(state.dismissedAnnouncements);
      next.add(id);
      localStorage.setItem('dismissed_announcements', JSON.stringify([...next]));
      return { dismissedAnnouncements: next };
    }),

  logout: () =>
    set({
      user: null,
      organizationId: null,
      pendingInvitations: [],
      announcements: [],
    }),
}));
